import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
} from "@chakra-ui/react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type props = {
  getCities: (currentP?: number) => Promise<void>;
  setIsCreateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editData: {};
  onSubmit: (editedData: any) => Promise<void>;
};

export default function CreateCity({
  getCities,
  setIsCreateModalOpen,
  editData,
  onSubmit,
}: props): JSX.Element {
  const [formData, setFormData] = useState<any>({
    name: "",
    cityCode: "",
    image: "",
    metroCancellation: false,
    operatingHours: {
      start: "", // Initialize start time as an empty string
      end: "", // Initialize end time as an empty string
    },
    gps: {
      latitude: "",
      longitude: "",
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toastNotify } = useContext(AppContext);

  useEffect(() => {
    if (editData) {
      setFormData(editData);
    }
  }, [editData]);

  const ValidateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.cityCode) newErrors.cityCode = "CityCode is required";
    if (!formData.image) newErrors.image = "image is required";
    if (formData.metroCancellation === "")
      newErrors.metroCancellation = "Metro Cancellation is required";
    if (!formData.operatingHours?.start)
      newErrors.startTime = "Start Time is required";
    if (!formData.operatingHours?.end)
      newErrors.endTime = "End Time is required";
    if (!formData.gps.latitude || isNaN(Number(formData.gps.latitude)))
      newErrors.latitude = "Latitude is required and must be a number";
    if (!formData.gps.longitude || isNaN(Number(formData.gps.longitude)))
      newErrors.longitude = "Longitude is required and must be a number";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Handle operating hours separately (start and end times)
    if (name === "start" || name === "end") {
      setFormData((prev: any) => ({
        ...prev,
        operatingHours: {
          ...prev.operatingHours,
          [name]: value, // Update the respective time
        },
      }));
      return;
    }

    if (name === "latitude" || name === "longitude") {
      setFormData((prev: any) => ({
        ...prev,
        gps: {
          ...prev.gps,
          [name]: value,
        },
      }));
      return;
    }

    // Specific logic for metroCancellation
    if (name === "metroCancellation") {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value === "true", // Convert to boolean
      }));
      return;
    }

    // Specific logic for cityCode to ensure "std:" prefix
    if (name === "cityCode") {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value.startsWith("std:") ? value : `std:${value}`,
      }));
      return;
    }

    // General case for other fields
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ValidateForm()) return;

    const payload = {
      ...formData,
      gps: {
        latitude: Number(formData.gps.latitude),
        longitude: Number(formData.gps.longitude),
      },
    };

    try {
      if (Object.keys(editData).length === 0) {
        const response: any = await appRequest("metro", "addCity", payload);
        if (response.status === "success") {
          toastNotify({
            title: "Success",
            message: "City added successfully",
            type: "success",
          });
          getCities();
          setIsCreateModalOpen(false);
        } else {
          toastNotify({
            title: "Error in adding City",
            message: response.message,
            type: "error",
          });
        }
      } else {
        await onSubmit(payload);
      }
    } catch (error: any) {
      console.error("error in adding city", error.message);
    }
  };

  return (
    <>
      <Box mt={4}>
        <form onSubmit={handleSubmit}>
          <Grid
            gap={6}
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            mb={8}
            p={5}
            shadow={"base"}
            rounded={"lg"}
            borderWidth={1}
          >
            <FormControl variant={"floating"} isInvalid={!!errors.name}>
              <Input
                id="name"
                name="name"
                placeholder=""
                value={formData.name}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>
            <FormControl variant={"floating"} isInvalid={!!errors.cityCode}>
              <InputGroup>
                <InputLeftAddon>std:</InputLeftAddon>
                <Input
                  id="cityCode"
                  name="cityCode"
                  placeholder=""
                  // value={formData.cityCode.replace("std:", "")}
                  value={(formData.cityCode || "").replace("std:", "")}
                  onChange={handleInputChange}
                />

                <FormLabel htmlFor="code">City Code</FormLabel>
              </InputGroup>
              <FormErrorMessage>{errors.cityCode}</FormErrorMessage>
            </FormControl>
            <FormControl variant={"floating"} isInvalid={!!errors.image}>
              <Input
                id="image"
                name="image"
                placeholder=""
                value={formData.image}
                onChange={handleInputChange}
              />

              <FormLabel htmlFor="image">image</FormLabel>
              <FormErrorMessage>{errors.image}</FormErrorMessage>
            </FormControl>
            <FormControl
              variant={"floating"}
              isInvalid={!!errors.metroCancellation}
            >
              <Select
                id="metroCancellation"
                placeholder=""
                name="metroCancellation"
                size={{ base: "sm", md: "md" }}
                variant="outline"
                value={formData.metroCancellation}
                onChange={handleInputChange}
              >
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
              <FormLabel htmlFor="metroCancellation">
                Is Cancellation allowed?
              </FormLabel>
              <FormErrorMessage>{errors.metroCancellation}</FormErrorMessage>
            </FormControl>
            <FormControl variant={"floating"} isInvalid={!!errors.startTime}>
              <Input
                id="start"
                name="start"
                type="time"
                placeholder=""
                value={formData?.operatingHours?.start}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="start">Start Time (HH:mm)</FormLabel>
              <FormErrorMessage>{errors.startTime}</FormErrorMessage>
            </FormControl>
            <FormControl variant={"floating"} isInvalid={!!errors.endTime}>
              <Input
                id="end"
                name="end"
                type="time"
                placeholder=""
                value={formData?.operatingHours?.end}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="end">End Time (HH:mm)</FormLabel>
              <FormErrorMessage>{errors.endTime}</FormErrorMessage>
            </FormControl>
            <FormControl variant={"floating"} isInvalid={!!errors.latitude}>
              <Input
                id="latitude"
                name="latitude"
                placeholder=""
                value={formData?.gps?.latitude}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="latitude">Latitude</FormLabel>
              <FormErrorMessage>{errors.latitude}</FormErrorMessage>
            </FormControl>
            <FormControl variant={"floating"} isInvalid={!!errors.longitude}>
              <Input
                id="longitude"
                name="longitude"
                placeholder=""
                value={formData?.gps?.longitude}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="longitude">Longitude</FormLabel>
              <FormErrorMessage>{errors.longitude}</FormErrorMessage>
            </FormControl>
          </Grid>
          <Flex justify={"end"} mt={4}>
            {/* <Button type="submit" colorScheme="teal">
              Submit
            </Button> */}
            <Button type="submit" colorScheme="teal">
              {Object.keys(editData).length === 0 ? "Submit" : "Update"}
            </Button>
          </Flex>
        </form>
      </Box>
    </>
  );
}
